import React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';

import { Theme, useMediaQuery } from '@mui/material';
import { School, Staff, StaffEntityType, StaffAudienceProps } from '../../../../../../../models/FormProps';
import { isEmpty } from '../../../../../../../utils/equality';
import { naturalStringSort, staffSort } from '../../../../../../../utils/sort';
import ScarecrowImage from '../../../../../../../../../assets/images/scarecrow.png';
import SchoolTable from '../../common/SchoolTable';
import PersonTable from '../../common/PersonTable';

interface Props {
  schools: Array<School>;
  onClickAddDataToPersonalise: () => void;
  viewableAudience: StaffAudienceProps;
  removeViewableEntitiesCallback: (viewableEntities: Array<School | Staff>) => void;
  viewOnly: boolean;
  isHQUser: boolean;
}

export default function SelectedStaffAudience(props: Props): React.ReactElement {
  const { schools, onClickAddDataToPersonalise, viewableAudience, removeViewableEntitiesCallback, viewOnly, isHQUser } = props;
  const isMobileOrTablet = useMediaQuery<Theme>(theme => theme.breakpoints.down('lg'));

  return isEmpty(viewableAudience.entities) ? renderNoStaffSelected() : renderSelectedStaffTable();

  function renderNoStaffSelected() {
    return !isMobileOrTablet && (
      <Stack spacing={2} direction="column" className="audience-editor-no-audience-selected">
        <img className="audience-editor-no-audience-selected-media" alt="" src={ScarecrowImage} />
        <Typography variant="body1">
          You have not selected any audience.
        </Typography>
      </Stack>
    );
  }

  function renderSelectedStaffTable() {
    const entitiesBySchoolID: Map<number, Array<School | Staff>> = viewableAudience.entities.reduce((acc, viewableEntity) => {
      const schoolID = viewableEntity.entity_type === StaffEntityType.SCHOOL
        ? viewableEntity.entity_id
        : (viewableEntity as Staff).school?.entity_id;

      if (!acc.has(schoolID)) {
        acc.set(schoolID, []);
      }

      acc.get(schoolID).push(viewableEntity);
      return acc;
    }, new Map<number, Array<School | Staff>>());

    return isMobileOrTablet ? renderMobileStudentsTable(entitiesBySchoolID) : renderDesktopStudentsTable(entitiesBySchoolID);
  }

  function renderDesktopStudentsTable(entitiesBySchoolID: Map<number, Array<School | Staff>>) {
    return (
      <Stack spacing={3}>
        <Stack
          spacing={2}
          sx={{
            maxHeight: 'calc(100vh - 56px - 78px - 32px - 16px - 68.25px - 62.25px)',
            overflowY: 'auto'
          }}
        >
          {isHQUser ? renderTablesBySchool(entitiesBySchoolID) : renderTables(viewableAudience.entities)}
        </Stack>
        <Box display="flex" justifyContent="right">
          <Button
            sx={{
              width: '300px'
            }}
            variant="text"
            size="large"
            onClick={onClickAddDataToPersonalise}
            startIcon={<AddIcon />}
          >Add data to personalise
          </Button>
        </Box>
      </Stack>
    );
  }

  function renderMobileStudentsTable(entitiesBySchoolID: Map<number, Array<School | Staff>>) {
    return (
      <Stack
        spacing={0}
        sx={{
          maxHeight: 'calc(100vh - 56px - 100px)', // minus footer height and top of the parent Drawer
          overflowY: 'auto'
        }}
      >
        <Box sx={{
          zIndex: 99,
          bgcolor: 'background.default',
          display: 'flex',
          justifyContent: 'right',
          py: 1,
          pl: 1,
          pr: 2,
          position: 'sticky'
        }}
        >
          <Button
            size="large"
            onClick={onClickAddDataToPersonalise}
            startIcon={<AddIcon />}
            sx={{ p: 0.5 }}
          >Add data to personalise
          </Button>
        </Box>
        {isHQUser ? renderTablesBySchool(entitiesBySchoolID) : renderTables(viewableAudience.entities)}
      </Stack>
    );
  }

  function renderTablesBySchool(entitiesBySchoolID: Map<number, Array<School | Staff>>) {
    const schoolsInAudience = schools.filter(school => entitiesBySchoolID.has(school.entity_id)).sort((a, b) => naturalStringSort(a.name, b.name));

    return (
      <Stack spacing={3} divider={<Divider orientation="horizontal" flexItem />}>
        {
          schoolsInAudience.map((school, index) => {
            const schoolName = school.name;
            const entities = entitiesBySchoolID.get(school.entity_id);

            return (
              <Box key={school.entity_id}>
                <Box sx={{ px: 2, py: index === 0 ? 1 : 0 }}>
                  <Typography sx={{ fontWeight: '700', textTransform: 'uppercase', py: 0.5 }}>{schoolName}</Typography>
                </Box>
                <Stack spacing={2}>
                  {renderTables(entities)}
                </Stack>
              </Box>
            );
          })
        }
      </Stack>
    );
  }

  function renderTables(entities: Array<School | Staff>) {
    const schoolEntities = entities.filter(viewableEntity => viewableEntity.entity_type === StaffEntityType.SCHOOL) as Array<School>;
    const staffEntities = entities.filter(viewableEntity => viewableEntity.entity_type === StaffEntityType.STAFF) as Array<Staff>;

    return (
      <>
        {schoolEntities.length === 1 && renderSchoolEntity(schoolEntities[0])}
        {staffEntities.length !== 0 && renderStaffEntities(staffEntities)}
      </>
    );
  }

  function renderSchoolEntity(viewableEntity: School) {
    return (
      <SchoolTable
        school={viewableEntity}
        tableHeader="ALL STAFF"
        pluralEntityName="staff"
        viewOnly={viewOnly}
        removeViewableEntitiesCallback={removeViewableEntitiesCallback}
      />
    );
  }

  function renderStaffEntities(viewableEntities: Array<Staff>) {
    return (
      <PersonTable
        tableHeader="Staff"
        pluralEntityName="staff"
        viewOnly={viewOnly}
        removeViewableEntitiesCallback={removeViewableEntitiesCallback}
        viewableEntities={viewableEntities.sort((a, b) => staffSort(a, b))}
        getDisplayName={(staff: Staff) => staff.name.toUpperCase()}
        getSecondaryInfo={(staff: Staff) => staff.email}
      />
    );
  }
}
